import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { resetPassword } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthOpen } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    if (!token) { toast.error("This reset link is invalid."); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password.");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-card border border-border w-full max-w-md p-8 text-center" data-testid="reset-done">
        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-secondary flex items-center justify-center"><CheckCircle2 size={40} className="text-[#15803D]" /></div>
        <h1 className="font-serif text-3xl tracking-tight mb-2">Password updated</h1>
        <p className="text-sm text-muted-foreground mb-6">You can now log in with your new password.</p>
        <button onClick={() => { setAuthOpen({ open: true, mode: "login" }); navigate("/"); }} data-testid="reset-done-login"
          className="w-full bg-oxford text-white py-3 text-sm font-medium hover:bg-[#2C5282] transition-colors">Log in</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="bg-card border border-border w-full max-w-md p-8">
        <h1 className="font-serif text-3xl tracking-tight mb-1">Choose a new password</h1>
        <p className="text-sm text-muted-foreground mb-6">Enter your new password. It needs to be at least 6 characters.</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" required placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-input"
            className="w-full border border-border px-3 py-3 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
          <input type="password" required placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-password-confirm"
            className="w-full border border-border px-3 py-3 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-oxford" />
          <button type="submit" disabled={loading} data-testid="reset-password-submit"
            className="w-full bg-oxford text-white py-3 text-sm font-medium hover:bg-[#2C5282] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 size={16} className="animate-spin" />} Update password
          </button>
        </form>
      </div>
    </div>
  );
}
